import type { LocalizedDict } from '../index'

// Seller Wallet → Subscription (Volkan 2026-11-01, plan
// docs/plans/2026-11-01-seller-subscription-plan.md part 3): what the seller
// pays, the card it is charged to, past invoices, and the warning strip.

type SellerSubscriptionStrings = {
  title: string
  description: (trialDays: number) => string
  loading: string
  loadError: string
  retry: string
  perMonth: string
  monthlyFee: string
  status: {
    trialing: string
    active: string
    unpaid: string
    canceled: string
    none: string
    exempt: string
  }
  trialEnds: (date: string) => string
  nextCharge: (date: string, amount: string) => string
  notStarted: string
  exempt: string
  canceled: string
  unpaidAlert: (date: string) => string
  pausedNoCard: string
  card: {
    title: string
    current: (brand: string, last4: string, exp: string) => string
    none: string
    noneTrial: string
    savedTitle: string
    use: string
    add: string
    addTitle: string
    addHelp: string
    save: string
    saving: string
    cancel: string
    saved: string
    declined: string
    failed: string
    stripeUnavailable: string
    notConfigured: string
  }
  invoices: {
    title: string
    empty: string
    date: string
    number: string
    amount: string
    status: string
    view: string
    states: Record<string, string>
  }
  strip: {
    unpaid: (date: string) => string
    trialNoCard: (date: string) => string
    action: string
  }
}

export const sellerSubscriptionI18n: LocalizedDict<SellerSubscriptionStrings> = {
  en: {
    title: 'Subscription',
    description: (days) =>
      `Your monthly PEXIFY membership. The first ${days} days are free; after that Stripe charges your card every month.`,
    loading: 'Loading your subscription…',
    loadError: 'Your subscription could not be loaded.',
    retry: 'Try again',
    perMonth: '/ month',
    monthlyFee: 'Monthly fee',
    status: {
      trialing: 'Free trial',
      active: 'Active',
      unpaid: 'Payment failed',
      canceled: 'Canceled',
      none: 'Not started',
      exempt: 'Free',
    },
    trialEnds: (date) => `Free trial ends on ${date}.`,
    nextCharge: (date, amount) => `Next charge: ${amount} on ${date}.`,
    notStarted: 'Your subscription has not started yet. PEXIFY will let you know before anything is charged.',
    exempt: 'Your account does not pay a subscription fee.',
    canceled: 'Your subscription is canceled. No further charges are made.',
    unpaidAlert: (date) =>
      `We could not charge your card. Add or update your card below. If it stays unpaid, order approval and label buying stop on ${date}.`,
    pausedNoCard: 'Your free trial ended without a card, so the subscription is paused. Add a card to continue.',
    card: {
      title: 'Payment card',
      current: (brand, last4, exp) => `${brand} •••• ${last4} · expires ${exp}`,
      none: 'No card on this subscription yet.',
      noneTrial: 'No card yet. Add one before your trial ends, or the subscription pauses.',
      savedTitle: 'Your saved cards',
      use: 'Use this card',
      add: 'Add a new card',
      addTitle: 'Add a card',
      addHelp: 'Nothing is charged now. The card is saved for your monthly subscription.',
      save: 'Save card',
      saving: 'Saving…',
      cancel: 'Cancel',
      saved: 'Card saved for your subscription.',
      declined: 'The card was declined.',
      failed: 'The card could not be saved. Please try again.',
      stripeUnavailable: 'Card and invoice details are not available right now. Please try again later.',
      notConfigured: 'Card payments are not available yet.',
    },
    invoices: {
      title: 'Invoices',
      empty: 'No invoices yet. The first one comes when your free trial ends.',
      date: 'Date',
      number: 'Invoice',
      amount: 'Amount',
      status: 'Status',
      view: 'View',
      states: { paid: 'Paid', open: 'Unpaid', void: 'Voided', uncollectible: 'Uncollectible', draft: 'Draft' },
    },
    strip: {
      unpaid: (date) =>
        `Your subscription payment failed. Update your card before ${date} to keep approving orders and buying labels.`,
      trialNoCard: (date) => `Your free trial ends on ${date}. Add a card so your subscription does not pause.`,
      action: 'Go to Subscription',
    },
  },
  tr: {
    title: 'Abonelik',
    description: (days) =>
      `PEXIFY aylık üyeliğiniz. İlk ${days} gün ücretsiz; sonrasında Stripe her ay kartınızdan çeker.`,
    loading: 'Aboneliğiniz yükleniyor…',
    loadError: 'Aboneliğiniz yüklenemedi.',
    retry: 'Tekrar dene',
    perMonth: '/ ay',
    monthlyFee: 'Aylık ücret',
    status: {
      trialing: 'Ücretsiz deneme',
      active: 'Aktif',
      unpaid: 'Ödeme alınamadı',
      canceled: 'İptal edildi',
      none: 'Başlamadı',
      exempt: 'Ücretsiz',
    },
    trialEnds: (date) => `Ücretsiz deneme ${date} tarihinde bitiyor.`,
    nextCharge: (date, amount) => `Sonraki çekim: ${date} · ${amount}.`,
    notStarted: 'Aboneliğiniz henüz başlamadı. Herhangi bir ücret çekilmeden önce PEXIFY size haber verir.',
    exempt: 'Hesabınız abonelik ücretinden muaf.',
    canceled: 'Aboneliğiniz iptal edildi. Başka ücret çekilmez.',
    unpaidAlert: (date) =>
      `Kartınızdan ödeme alınamadı. Aşağıdan kart ekleyin veya güncelleyin. Ödenmezse ${date} tarihinde sipariş onayı ve etiket alımı durur.`,
    pausedNoCard: 'Ücretsiz deneme kartsız bittiği için abonelik durdu. Devam etmek için kart ekleyin.',
    card: {
      title: 'Ödeme kartı',
      current: (brand, last4, exp) => `${brand} •••• ${last4} · son kullanma ${exp}`,
      none: 'Bu abonelikte henüz kart yok.',
      noneTrial: 'Henüz kart yok. Deneme bitmeden ekleyin; eklemezseniz abonelik durur.',
      savedTitle: 'Kayıtlı kartlarınız',
      use: 'Bu kartı kullan',
      add: 'Yeni kart ekle',
      addTitle: 'Kart ekle',
      addHelp: 'Şimdi hiçbir ücret çekilmez. Kart aylık aboneliğiniz için kaydedilir.',
      save: 'Kartı kaydet',
      saving: 'Kaydediliyor…',
      cancel: 'Vazgeç',
      saved: 'Kart aboneliğiniz için kaydedildi.',
      declined: 'Kart reddedildi.',
      failed: 'Kart kaydedilemedi. Lütfen tekrar deneyin.',
      stripeUnavailable: 'Kart ve fatura bilgileri şu an alınamıyor. Lütfen daha sonra tekrar deneyin.',
      notConfigured: 'Kartla ödeme henüz kullanılamıyor.',
    },
    invoices: {
      title: 'Faturalar',
      empty: 'Henüz fatura yok. İlk fatura ücretsiz deneme bitince gelir.',
      date: 'Tarih',
      number: 'Fatura',
      amount: 'Tutar',
      status: 'Durum',
      view: 'Görüntüle',
      states: { paid: 'Ödendi', open: 'Ödenmedi', void: 'İptal', uncollectible: 'Tahsil edilemedi', draft: 'Taslak' },
    },
    strip: {
      unpaid: (date) =>
        `Abonelik ödemeniz alınamadı. Sipariş onayı ve etiket alımı durmasın diye kartınızı ${date} tarihine kadar güncelleyin.`,
      trialNoCard: (date) => `Ücretsiz denemeniz ${date} tarihinde bitiyor. Abonelik durmasın diye kart ekleyin.`,
      action: 'Aboneliğe git',
    },
  },
}
